import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Trash2,
  AlertTriangle,
  CheckCircle2,
  MessageSquare,
  Paperclip,
  Eye,
  Download,
  X,
  Calendar,
  User,
  Clock,
  FileText,
  Image,
  File,
  UserCircle,
  Users,
  Briefcase,
  Phone,
  Mail,
} from "lucide-react";
import { getComplaint, deleteComplaint } from "../../api/complaints";
import { formatDateTime } from "../../lib/records";
import { Card, Button, Skeleton, EmptyState, Avatar, Field, Textarea } from "../../components/ui";
import { Modal } from "../../components/Modal";
import { StatusChip, PriorityChip } from "../../components/Chips";

export default function ComplaintDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [complaint, setComplaint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    getComplaint(id)
      .then((res) => setComplaint(res.data))
      .catch((err) => {
        if (err?.response?.status === 403 || err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [id]);

  async function handleDelete() {
    if (reason.trim().length < 5) {
      setReasonError("Please provide at least 5 characters explaining why.");
      return;
    }
    setDeleting(true);
    try {
      await deleteComplaint(id, reason.trim());
      navigate("/complaints");
    } catch (err) {
      setReasonError(err?.response?.data?.message || "Could not delete this complaint.");
    } finally {
      setDeleting(false);
    }
  }

  const getFileIcon = (url) => {
    const ext = url?.split('.').pop()?.toLowerCase() || '';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return Image;
    if (['pdf'].includes(ext)) return FileText;
    if (['mp4', 'mov', 'avi', 'webm'].includes(ext)) return File;
    return File;
  };

  const getFileType = (url) => {
    const ext = url?.split('.').pop()?.toLowerCase() || '';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'image';
    if (['pdf'].includes(ext)) return 'pdf';
    if (['mp4', 'mov', 'avi', 'webm'].includes(ext)) return 'video';
    return 'file';
  };

  const openEmployeeDetails = (employee) => {
    if (employee) {
      setSelectedEmployee(employee);
    }
  };

  const formatDate = (dateStr) => formatDateTime(dateStr);

  const renderAttachmentGrid = (attachments, keyPrefix = "att") => {
    if (!attachments?.length) return null;
    return (
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-4 gap-2 sm:gap-3">
        {attachments.slice(0, 8).map((attachment, index) => {
          const FileIcon = getFileIcon(attachment.url);
          const fileType = getFileType(attachment.url);
          const isImage = fileType === 'image';
          const isVideo = fileType === 'video';

          return (
            <div
              key={`${keyPrefix}-${index}`}
              onClick={() => (isImage || isVideo) && setSelectedAttachment(attachment)}
              className={`group relative rounded-lg sm:rounded-xl border border-gray-200 overflow-hidden bg-white hover:border-emerald-300 hover:shadow-md transition-all duration-200 aspect-square ${
                (isImage || isVideo) ? 'cursor-pointer' : 'cursor-default'
              }`}
            >
              {isImage ? (
                <div className="w-full h-full relative">
                  <img
                    src={attachment.url}
                    alt={`Attachment ${index + 1}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-200 flex items-center justify-center">
                    <Eye className="w-4 sm:w-6 h-4 sm:h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                  </div>
                  <div className="absolute top-1 left-1 sm:top-2 sm:left-2">
                    <span className="text-[8px] sm:text-[10px] font-medium bg-black/50 text-white px-1 sm:px-2 py-0.5 rounded-full backdrop-blur-sm">
                      {attachment.type || 'Image'}
                    </span>
                  </div>
                </div>
              ) : isVideo ? (
                <div className="w-full h-full relative bg-gray-900 flex items-center justify-center">
                  <video className="w-full h-full object-cover" src={attachment.url} muted />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <svg className="w-4 h-4 sm:w-6 sm:h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 0C4.477 0 0 4.477 0 10s4.477 10 10 10 10-4.477 10-10S15.523 0 10 0zm-2 14.5v-9l6 4.5-6 4.5z" />
                      </svg>
                    </div>
                  </div>
                  <div className="absolute top-1 left-1 sm:top-2 sm:left-2">
                    <span className="text-[8px] sm:text-[10px] font-medium bg-black/50 text-white px-1 sm:px-2 py-0.5 rounded-full backdrop-blur-sm">
                      {attachment.type || 'Video'}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50 group-hover:bg-emerald-50/50 transition-colors p-2">
                  <FileIcon size={20} className="text-gray-400 group-hover:text-emerald-500 transition-colors" />
                  <span className="text-[8px] text-gray-500 mt-0.5 truncate w-full text-center">
                    {attachment.url?.split('/').pop()?.slice(0, 10) || `File`}
                  </span>
                </div>
              )}
              {!isImage && !isVideo && (
                <a
                  href={attachment.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black/40 transition-opacity duration-200"
                >
                  <Download size={16} className="text-white" />
                </a>
              )}
            </div>
          );
        })}
        {attachments.length > 8 && (
          <div className="aspect-square rounded-lg sm:rounded-xl border border-gray-200 bg-gray-50 flex items-center justify-center text-xs text-gray-500">
            +{attachments.length - 8} more
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
        <div className="px-4 sm:px-6 py-4 sm:py-6 lg:px-8 max-w-4xl mx-auto space-y-4">
          <Skeleton className="h-6 w-28 sm:w-32" />
          <Card className="p-4 sm:p-6 bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-xl shadow-emerald-100/50">
            <Skeleton className="h-5 sm:h-6 w-2/3" />
            <Skeleton className="mt-2 sm:mt-3 h-4 w-1/3" />
          </Card>
        </div>
      </div>
    );
  }

  if (notFound || !complaint) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
        <div className="px-4 sm:px-6 py-4 sm:py-6 lg:px-8 max-w-4xl mx-auto">
          <EmptyState
            icon={AlertTriangle}
            title="Complaint not found"
            description="This complaint may have been removed or doesn't belong to your organization."
            action={
              <Link to="/complaints">
                <Button variant="secondary" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                  Back to complaints
                </Button>
              </Link>
            }
          />
        </div>
      </div>
    );
  }

  const submission = complaint.submission;
  const resolution = complaint.resolution;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
      <div className="px-4 sm:px-6 py-4 sm:py-6 lg:px-8 max-w-4xl mx-auto">
        <Link to="/complaints" className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:underline transition-colors mb-4 sm:mb-5">
          <ArrowLeft size={14} /> Back to complaints
        </Link>

        <Card className="p-4 sm:p-6 bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-xl shadow-emerald-100/50">
          {/* Header */}
          <div className="flex flex-col gap-3 sm:gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                <span className="text-[10px] sm:text-xs font-medium text-gray-500 bg-gray-100 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full">
                  {complaint.complaintId}
                </span>
                <span className="text-[10px] sm:text-xs text-gray-400">•</span>
                <span className="text-[10px] sm:text-xs text-gray-500">{complaint.category}</span>
                <span className="text-[10px] sm:text-xs text-gray-400">•</span>
                <span className="text-[10px] sm:text-xs text-gray-500">{complaint.partner?.name}</span>
              </div>
              <h1 className="mt-1.5 sm:mt-2 text-lg sm:text-2xl font-bold text-gray-800 line-clamp-2">{complaint.title}</h1>
              <div className="mt-2 sm:mt-3 flex flex-wrap items-center gap-1.5 sm:gap-2">
                <PriorityChip priority={complaint.priority} />
                <StatusChip status={complaint.status} />
                {complaint.createdAt && (
                  <span className="flex items-center gap-1 text-[10px] sm:text-xs text-gray-400 bg-gray-50 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full">
                    <Calendar size={11} />
                    {formatDate(complaint.createdAt)}
                  </span>
                )}
              </div>
            </div>
            <Button 
              variant="destructive" 
              onClick={() => setDeleteOpen(true)}
              className="bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-500/20 hover:shadow-red-500/30 transition-all text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2 w-full sm:w-auto"
            >
              <Trash2 size={14} className="mr-1" /> Delete
            </Button>
          </div>

          {/* Grievance Description */}
          <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-emerald-50/50 rounded-xl border border-emerald-100/50">
            <p className="text-sm sm:text-base text-gray-700 leading-relaxed">{complaint.grievance}</p>
          </div>

          {/* Employee Details Section */}
          <div className="mt-4 sm:mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {/* Raiser Details */}
            <div 
              className="p-3 sm:p-4 bg-white rounded-xl border border-gray-200 hover:border-emerald-300 hover:shadow-md transition-all cursor-pointer group"
              onClick={() => openEmployeeDetails(complaint.raiser)}
            >
              <div className="flex items-center gap-2 sm:gap-3">
                <Avatar name={complaint.raiser?.name || "Unknown"} size={40} className="ring-2 ring-emerald-100" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                    <h4 className="text-xs sm:text-sm font-semibold text-gray-800 truncate">{complaint.raiser?.name || "Unknown"}</h4>
                    <span className="text-[8px] sm:text-[10px] font-medium text-emerald-600 bg-emerald-50 px-1.5 sm:px-2 py-0.5 rounded-full">Raiser</span>
                  </div>
                  {complaint.raiser?.designation && (
                    <p className="text-[10px] sm:text-xs text-gray-500 flex items-center gap-1 truncate">
                      <Briefcase size={11} />
                      {complaint.raiser.designation}
                    </p>
                  )}
                  {complaint.raiser?.phone && (
                    <p className="text-[10px] sm:text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                      <Phone size={10} />
                      {complaint.raiser.phone}
                    </p>
                  )}
                  <p className="text-[10px] sm:text-xs text-gray-400 mt-0.5 italic">Raised this complaint</p>
                </div>
                <UserCircle size={18} className="text-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity hidden sm:block" />
              </div>
            </div>

            <div 
              className="p-3 sm:p-4 bg-white rounded-xl border border-gray-200 hover:border-emerald-300 hover:shadow-md transition-all cursor-pointer group"
              onClick={() => openEmployeeDetails(complaint.assignee)}
            >
              <div className="flex items-center gap-2 sm:gap-3">
                <Avatar name={complaint.assignee?.name || "Unassigned"} size={40} className="ring-2 ring-emerald-100" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                    <h4 className="text-xs sm:text-sm font-semibold text-gray-800 truncate">{complaint.assignee?.name || "Unassigned"}</h4>
                    <span className="text-[8px] sm:text-[10px] font-medium text-blue-600 bg-blue-50 px-1.5 sm:px-2 py-0.5 rounded-full">Assignee</span>
                  </div>
                  {complaint.assignee?.designation && (
                    <p className="text-[10px] sm:text-xs text-gray-500 flex items-center gap-1 truncate">
                      <Briefcase size={11} />
                      {complaint.assignee.designation}
                    </p>
                  )}
                  {complaint.assignee?.phone && (
                    <p className="text-[10px] sm:text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                      <Phone size={10} />
                      {complaint.assignee.phone}
                    </p>
                  )}
                  <p className="text-[10px] sm:text-xs text-gray-400 mt-0.5 italic">Assigned to resolve this complaint</p>
                </div>
                <UserCircle size={18} className="text-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity hidden sm:block" />
              </div>
            </div>
          </div>

          {/* Attachments Section */}
          {complaint.attachments?.length > 0 && (
            <div className="mt-4 sm:mt-6">
              <h3 className="text-xs sm:text-sm font-semibold text-gray-700 flex items-center gap-1.5 sm:gap-2 mb-2 sm:mb-3">
                <Paperclip size={14} className="text-emerald-500" />
                Attachments ({complaint.attachments.length})
              </h3>
              {renderAttachmentGrid(complaint.attachments, "att")}
            </div>
          )}

          {/* Work Submission Section */}
          {submission && (
            <div className="mt-4 sm:mt-6 border-t border-emerald-100/60 pt-4 sm:pt-6">
              <div className="mb-3 sm:mb-4">
                <h3 className="text-xs sm:text-sm font-semibold text-gray-700 flex items-center gap-1.5 sm:gap-2">
                  <MessageSquare size={14} className="text-emerald-500" />
                  Work Submission
                </h3>
                <p className="text-[10px] sm:text-xs text-gray-400 mt-0.5 ml-5 sm:ml-6">
                  Proof of work submitted by the assignee for reviewer approval
                </p>
              </div>

              <div className="p-3 sm:p-4 bg-white rounded-xl border border-gray-200">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div
                    className="flex items-center gap-2 cursor-pointer group"
                    onClick={() => openEmployeeDetails(submission.submittedBy)}
                  >
                    <Avatar name={submission.submittedBy?.name || "Unknown"} size={32} className="ring-2 ring-emerald-100" />
                    <div>
                      <p className="text-xs sm:text-sm font-semibold text-gray-800 group-hover:text-emerald-600 transition-colors">
                        {submission.submittedBy?.name || "Unknown"}
                      </p>
                      {submission.submittedBy?.designation && (
                        <p className="text-[10px] sm:text-xs text-gray-500 flex items-center gap-1">
                          <Briefcase size={10} />
                          {submission.submittedBy.designation}
                        </p>
                      )}
                    </div>
                  </div>
                  {submission.submittedAt && (
                    <span className="flex items-center gap-1 text-[10px] sm:text-xs text-gray-400 bg-gray-50 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full">
                      <Clock size={11} />
                      {formatDate(submission.submittedAt)}
                    </span>
                  )}
                </div>

                {submission.comment && (
                  <div className="mt-3 p-2.5 sm:p-3 bg-emerald-50/50 rounded-lg border border-emerald-100/50">
                    <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">{submission.comment}</p>
                  </div>
                )}

                {submission.attachments?.length > 0 && (
                  <div className="mt-3 sm:mt-4">
                    <p className="text-[10px] sm:text-xs font-medium text-gray-500 flex items-center gap-1 mb-1">
                      <Paperclip size={12} className="text-emerald-500" />
                      Proof ({submission.attachments.length})
                    </p>
                    <p className="text-[10px] sm:text-xs text-gray-400 mb-2">
                      Photos/videos attached as evidence that the work was completed
                    </p>
                    {renderAttachmentGrid(submission.attachments, "sub")}
                  </div>
                )}
              </div>
            </div>
          )}

          {resolution?.resolvedBy && (
            <div className="mt-4 sm:mt-6 border-t border-emerald-100/60 pt-4 sm:pt-6">
              <h3 className="text-xs sm:text-sm font-semibold text-gray-700 flex items-center gap-1.5 sm:gap-2 mb-3 sm:mb-4">
                <CheckCircle2 size={14} className="text-emerald-500" />
                Resolution
              </h3>
              <div
                className="p-3 sm:p-4 bg-white rounded-xl border border-gray-200 flex items-center justify-between gap-2 flex-wrap cursor-pointer group"
                onClick={() => openEmployeeDetails(resolution.resolvedBy)}
              >
                <div className="flex items-center gap-2">
                  <Avatar name={resolution.resolvedBy?.name || "Unknown"} size={32} className="ring-2 ring-emerald-100" />
                  <div>
                    <p className="text-xs sm:text-sm font-semibold text-gray-800 group-hover:text-emerald-600 transition-colors">
                      {resolution.resolvedBy?.name || "Unknown"}
                    </p>
                    {resolution.resolvedBy?.designation && (
                      <p className="text-[10px] sm:text-xs text-gray-500">{resolution.resolvedBy.designation}</p>
                    )}
                  </div>
                </div>
                {resolution.resolvedAt && (
                  <span className="flex items-center gap-1 text-[10px] sm:text-xs text-gray-400 bg-gray-50 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full">
                    <Clock size={11} />
                    {formatDate(resolution.resolvedAt)}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Timeline Section */}
          {complaint.statusHistory?.length > 0 && (
            <div className="mt-4 sm:mt-6 border-t border-emerald-100/60 pt-4 sm:pt-6">
              <h3 className="text-xs sm:text-sm font-semibold text-gray-700 flex items-center gap-1.5 sm:gap-2 mb-3 sm:mb-4">
                <Clock size={14} className="text-emerald-500" />
                Timeline
              </h3>
              <div className="relative pl-5 sm:pl-6 space-y-3 sm:space-y-4">
                {complaint.statusHistory.map((history, index) => (
                  <div key={index} className="relative">
                    {index < complaint.statusHistory.length - 1 && (
                      <div className="absolute left-[-1.5px] top-3 w-0.5 h-full bg-emerald-200" />
                    )}
                    <div className="flex gap-2 sm:gap-3">
                      <div className="relative z-10 mt-1">
                        <div className={`h-2.5 w-2.5 sm:h-3 sm:w-3 rounded-full ring-2 ${
                          history.status === 'OPEN' ? 'bg-blue-500 ring-blue-100' :
                          history.status === 'ASSIGNED' ? 'bg-yellow-500 ring-yellow-100' :
                          history.status === 'UNDER_REVIEW' ? 'bg-purple-500 ring-purple-100' :
                          history.status === 'RESOLVED' ? 'bg-green-500 ring-green-100' :
                          'bg-gray-500 ring-gray-100'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs sm:text-sm font-medium text-gray-700 capitalize">
                          {history.status.replace(/_/g, ' ').toLowerCase()}
                        </p>
                        {history.note && (
                          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{history.note}</p>
                        )}
                        <p className="text-[10px] sm:text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                          <Clock size={10} />
                          {formatDate(history.changedAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>

        <Modal
          open={!!selectedEmployee}
          onClose={() => setSelectedEmployee(null)}
          title="Employee Details"
          maxWidth="max-w-md"
          className="bg-white/95 backdrop-blur-sm border border-emerald-100 shadow-2xl shadow-emerald-200/50"
        >
          {selectedEmployee && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-emerald-50/50 rounded-xl border border-emerald-100">
                <Avatar name={selectedEmployee.name || "Unknown"} size={56} className="ring-2 ring-emerald-200" />
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-gray-800">{selectedEmployee.name || "Unknown"}</h3>
                  {selectedEmployee.designation && (
                    <p className="text-xs sm:text-sm text-gray-600">{selectedEmployee.designation}</p>
                  )}
                  {selectedEmployee.id && (
                    <p className="text-[10px] sm:text-xs text-gray-400 mt-0.5">ID: {selectedEmployee.id}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2 sm:space-y-3">
                {selectedEmployee.phone && (
                  <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-white rounded-xl border border-gray-200">
                    <div className="p-1.5 sm:p-2 bg-emerald-100 rounded-lg">
                      <Phone size={14} className="text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-[10px] sm:text-xs text-gray-500">Phone</p>
                      <p className="text-xs sm:text-sm font-medium text-gray-800">{selectedEmployee.phone}</p>
                    </div>
                  </div>
                )}
                
                {selectedEmployee.email && (
                  <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-white rounded-xl border border-gray-200">
                    <div className="p-1.5 sm:p-2 bg-emerald-100 rounded-lg">
                      <Mail size={14} className="text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-[10px] sm:text-xs text-gray-500">Email</p>
                      <p className="text-xs sm:text-sm font-medium text-gray-800">{selectedEmployee.email}</p>
                    </div>
                  </div>
                )}

                {selectedEmployee.designation && (
                  <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-white rounded-xl border border-gray-200">
                    <div className="p-1.5 sm:p-2 bg-emerald-100 rounded-lg">
                      <Briefcase size={14} className="text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-[10px] sm:text-xs text-gray-500">Designation</p>
                      <p className="text-xs sm:text-sm font-medium text-gray-800">{selectedEmployee.designation}</p>
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={() => setSelectedEmployee(null)}
                className="w-full py-2 sm:py-2.5 bg-gradient-to-r from-emerald-500 to-green-600 text-white font-medium rounded-xl shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 hover:scale-[1.02] transition-all duration-200 text-sm"
              >
                Close
              </button>
            </div>
          )}
        </Modal>

        <Modal
          open={!!selectedAttachment}
          onClose={() => setSelectedAttachment(null)}
          title="Attachment Preview"
          maxWidth="max-w-4xl"
          className="bg-white/95 backdrop-blur-sm border border-emerald-100 shadow-2xl shadow-emerald-200/50"
        >
          {selectedAttachment && (
            <div className="relative">
              {getFileType(selectedAttachment.url) === 'image' ? (
                <img
                  src={selectedAttachment.url}
                  alt="Attachment preview"
                  className="w-full h-auto max-h-[60vh] sm:max-h-[70vh] object-contain rounded-lg"
                />
              ) : getFileType(selectedAttachment.url) === 'video' ? (
                <video
                  src={selectedAttachment.url}
                  controls
                  className="w-full h-auto max-h-[60vh] sm:max-h-[70vh] rounded-lg"
                  autoPlay={false}
                />
              ) : (
                <div className="flex flex-col items-center justify-center py-8 sm:py-12">
                  <FileText size={48} className="text-gray-400" />
                  <p className="mt-3 sm:mt-4 text-sm text-gray-600">Preview not available for this file type.</p>
                </div>
              )}
              <div className="mt-3 sm:mt-4 flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-2 sm:gap-4">
                <a
                  href={selectedAttachment.url}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-lg hover:shadow-lg hover:shadow-emerald-500/25 transition-all text-sm"
                >
                  <Download size={14} />
                  Download
                </a>
                <button
                  onClick={() => setSelectedAttachment(null)}
                  className="flex items-center justify-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors text-sm"
                >
                  <X size={14} />
                  Close
                </button>
              </div>
            </div>
          )}
        </Modal>

        {/* Delete Modal */}
        <Modal
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          title="Delete complaint"
          description="This soft-deletes the complaint and records your reason for the audit trail."
          maxWidth="max-w-md"
          className="bg-white/95 backdrop-blur-sm border border-emerald-100 shadow-2xl shadow-emerald-200/50"
        >
          <Field label="Reason" required error={reasonError} hint="5–1000 characters">
            <Textarea
              rows={4}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setReasonError("");
              }}
              placeholder="e.g. Duplicate complaint — same issue filed twice by mistake."
              className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 rounded-xl transition-all text-sm"
            />
          </Field>
          <div className="mt-4 flex flex-col sm:flex-row justify-end gap-2.5">
            <Button variant="ghost" onClick={() => setDeleteOpen(false)} className="text-gray-600 hover:text-gray-800 w-full sm:w-auto order-2 sm:order-1">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} loading={deleting} className="bg-red-500 hover:bg-red-600 w-full sm:w-auto order-1 sm:order-2">
              Delete complaint
            </Button>
          </div>
        </Modal>
      </div>
    </div>
  );
}